// Jazz comping rhythms for the chord playback: the Charleston and its
// displaced cousin, pushed chord changes, and a freestyle mix that adds
// chromatic approach stabs. Like the bass embellishments this is playback
// only — the sheets always show the clean chart — and every choice is
// deterministic (golden-ratio spread, never RNG) so a form plays the same
// way every time.

import { PHI, spread } from './walkingBass';
import { planCompRhythm, type CompFeel, type CompRhythmNote } from './compRhythm';
import type { PassKind } from './performance';

export type { CompFeel } from './compRhythm';

export type CompStyle =
  | 'held'
  | 'sparse'
  | 'charleston'
  | 'reverse'
  | 'push'
  | 'freestyle'
  | 'funk'
  | 'beat-two'
  | 'interactive';

export const COMP_STYLES: { id: CompStyle; label: string; hint: string }[] = [
  { id: 'held', label: 'Held chords', hint: 'One strum per chord, held to the change' },
  {
    id: 'sparse',
    label: 'Mostly held',
    hint: 'Held chords with the occasional push or re-statement — the amount sets how occasional',
  },
  { id: 'charleston', label: 'Charleston', hint: 'The classic figure: beat 1 and the “and” of 2' },
  {
    id: 'reverse',
    label: 'Reverse Charleston',
    hint: 'Displaced: the “and” of 2, answered on beat 4',
  },
  { id: 'push', label: 'Pushes', hint: 'Every chord change anticipated by an eighth' },
  { id: 'freestyle', label: 'Freestyle', hint: 'A mix of figures with chromatic approach stabs' },
  {
    id: 'funk',
    label: 'Funk',
    hint: 'A repeating two-bar groove with straight sixteenths and clipped replies',
  },
  {
    id: 'interactive',
    label: 'Interactive',
    hint: 'Phrases with space — follows the written lead when enabled; supports your solo when muted',
  },
  {
    id: 'beat-two',
    label: 'Beat-2 answers',
    hint: 'Let the bass lead: answer on beat 2 in whole-chord 4/4 bars, keeping phrase rests',
  },
];

/** Where each chord sits in the form (indices must match the playback events). */
export interface CompChordSpan {
  startBeat: number;
  durationBeats: number;
}

/** Which hand a hit strikes, for instruments that split the voicing. */
export type CompHand = 'left' | 'right' | 'both';

export interface CompHit {
  /** Index into the chord-event array this hit strikes. */
  chordIndex: number;
  /** Absolute in-form beat; fractional hits are offbeats (the player swings them). */
  beat: number;
  /** Ring time in beats before the hit is damped; Infinity = let it ring. */
  durationBeats: number;
  /** Loudness 0–1 (offbeats and stabs sit under the downbeats). */
  velocity: number;
  /** Chromatic approach stab: strike the same shape shifted ±1 fret. */
  fretShift?: number;
  /** Interactive style, piano only: per-note roll for this strike, seconds —
   * tight stabs, gentle pads, and the occasional slow expressive sweep. The
   * guitar path ignores it. */
  rollSec?: number;
  /** Interactive style, piano only: strike the chord's LIFT voicing — the
   * nearest inversion above — instead of the walk's (Crook: voicings may be
   * changed at the player's discretion within a chord). Statements never
   * lift; the guitar path ignores it. */
  lift?: boolean;
  /**
   * Per-hand realization (interactive style): 'both' states the chord,
   * 'right' answers over the ringing left hand. Absent = both. Only
   * meaningful when the striking instrument splits its voicing into hands
   * (the piano's two-hand families); the guitar strikes the whole grip
   * regardless.
   */
  hand?: CompHand;
  /** Straight-grid styles keep their attacks and releases even in a mixed band. */
  swing?: false;
}

export interface CompOptions {
  style: CompStyle;
  beatsPerBar?: number;
  /** Freestyle only: how often approach stabs fire (0–1). */
  amount?: number;
  /**
   * The chart changes meter mid-form: the fixed bar grid below would land
   * every figure off the barline after the change, so fall back to pushes
   * (which follow the chords, not the grid).
   */
  mixedMeter?: boolean;
  /**
   * Beat where the first FULL bar begins. A leading anacrusis offsets every
   * barline, so the figures must be laid from here — on the naive 0-based
   * grid a half-beat pickup puts every hit an eighth early for the whole
   * tune, striking each chord before it starts. Nothing is emitted inside
   * the pickup: the anacrusis is melody-only.
   */
  barOffset?: number;
  /** Interactive only: the written melody the planner comps AROUND. */
  melody?: CompRhythmNote[];
  /** Interactive only: chorus boundaries for the intensity arc. */
  passStarts?: number[];
  /** Interactive only: what each pass is under an itinerary (head / solo /
   * head-out), aligned with `passStarts`. */
  passKinds?: readonly PassKind[];
  /** Interactive only: arrangement tempo (picks the ballad/medium/fast vocabulary). */
  bpm?: number;
  /** Interactive only: the band's time-feel — a latin bass flips the engine
   * into McNeely's percussive quoted-figure role. Default swing. */
  feel?: CompFeel;
}

// Bar-relative figures the freestyle picks from (4/4). Positions in beats.
const FIGURES: number[][] = [
  [0, 1.5], // Charleston
  [1.5, 3], // reverse Charleston
  [0, 2.5],
  [0.5, 2],
  [0, 1.5, 3],
  [0], // let one bar breathe
];

/**
 * The chord a hit should strike: normally the chord sounding at that beat, but
 * a hit landing within half a beat of the NEXT chord's onset anticipates it —
 * hitting the upcoming change early is the whole point of these figures.
 */
function chordIndexAt(chords: CompChordSpan[], beat: number, continuousOnly = false): number {
  let sounding = -1;
  for (let i = 0; i < chords.length; i++) {
    const c = chords[i];
    if (c.startBeat <= beat && beat < c.startBeat + c.durationBeats) sounding = i;
    if (
      beat < c.startBeat &&
      c.startBeat - beat <= 0.5 + 1e-6 &&
      (!continuousOnly ||
        (sounding >= 0 &&
          chords[sounding].startBeat + chords[sounding].durationBeats >= c.startBeat - 1e-6))
    )
      return i;
  }
  return sounding;
}

/** Deterministically pick one of n options for a bar (golden-ratio hash). */
function pickFor(bar: number, n: number, salt: number): number {
  const frac = ((((bar + 1) * PHI + salt) % 1) + 1) % 1;
  return Math.min(n - 1, Math.floor(frac * n));
}

function hitsFromFigure(
  chords: CompChordSpan[],
  barStart: number,
  beatsPerBar: number,
  figure: number[],
): CompHit[] {
  const hits: CompHit[] = [];
  figure.forEach((pos, i) => {
    const beat = barStart + pos;
    const chordIndex = chordIndexAt(chords, beat);
    if (chordIndex < 0) return;
    // Ring to just shy of the figure's next hit; the bar's last hit carries
    // over the barline (the next bar's first hit re-strikes anyway).
    const next = i + 1 < figure.length ? figure[i + 1] : beatsPerBar + 1;
    hits.push({
      chordIndex,
      beat,
      durationBeats: Math.max(0.4, next - pos - 0.12),
      velocity: pos % 1 === 0 ? 1 : 0.85,
    });
  });
  return hits;
}

export interface BeatTwoAnswerOptions {
  beatsPerBar?: number;
  mixedMeter?: boolean;
  barOffset?: number;
  /** Whole bars must fit inside a selected interval; omitted means all bars. */
  ranges?: readonly { fromBeat: number; toBeat: number }[];
}

/** Rephrase existing statements, without inventing attacks or rebinding harmony.
 * A chord span is the source of truth: no inference from slash basses or titles.
 */
export function applyBeatTwoAnswers(
  chords: CompChordSpan[],
  hits: CompHit[],
  opts: BeatTwoAnswerOptions = {},
): CompHit[] {
  if ((opts.beatsPerBar ?? 4) !== 4 || opts.mixedMeter) return hits;
  const barOffset = opts.barOffset ?? 0;
  const groups = new Map<number, CompHit[]>();
  for (const hit of hits) {
    const bar = Math.floor((hit.beat - barOffset) / 4);
    if (bar < 0) continue;
    const start = barOffset + bar * 4;
    const end = start + 4;
    if (
      opts.ranges &&
      !opts.ranges.some(({ fromBeat, toBeat }) => start >= fromBeat - 1e-6 && end <= toBeat + 1e-6)
    )
      continue;
    const chord = chords[hit.chordIndex];
    if (
      !chord ||
      chord.startBeat > start + 1e-6 ||
      chord.startBeat + chord.durationBeats < end - 1e-6
    )
      continue;
    // An anticipating hit belongs to the NEXT chord, which does not cover
    // this whole bar. Overlapping/ambiguous chord spans do not qualify either.
    if (
      chords.some(
        (other, index) =>
          index !== hit.chordIndex &&
          other.startBeat < end - 1e-6 &&
          other.startBeat + other.durationBeats > start + 1e-6,
      )
    )
      continue;
    const group = groups.get(start) ?? [];
    group.push(hit);
    groups.set(start, group);
  }
  if (!groups.size) return hits;

  const replaced = new Set([...groups.values()].flat());
  const movedEarlier = new Set<CompHit>();
  const answers = [...groups].map(([start, group]): CompHit => {
    // Collapse a repeated figure to its first statement, keeping that
    // statement's velocity and voicing metadata. Preserve its final planned
    // release; a short figure that already ended keeps its original gate.
    const first = group[0];
    const beat = start + 1;
    const release = Math.max(...group.map((hit) => hit.beat + hit.durationBeats));
    const chord = chords[first.chordIndex];
    const answer: CompHit = {
      ...first,
      beat,
      durationBeats: Math.min(
        release > beat ? release - beat : first.durationBeats,
        start + 4 - beat,
        chord.startBeat + chord.durationBeats - beat,
      ),
    };
    if (beat < first.beat) movedEarlier.add(answer);
    return answer;
  });
  const answerSet = new Set(answers);
  const result = [...hits.filter((hit) => !replaced.has(hit)), ...answers].sort(
    (a, b) => a.beat - b.beat,
  );
  // Moving an answer earlier can meet an incoming anticipation. Release the
  // preceding chord at the new attack, as well as bounding the answer itself.
  return result.map((hit, index) => {
    const next = result[index + 1];
    // Existing overlaps outside this rephrasing are not ours to rewrite.
    if (!next || (!answerSet.has(hit) && !movedEarlier.has(next))) return hit;
    return { ...hit, durationBeats: Math.min(hit.durationBeats, next.beat - hit.beat) };
  });
}

/**
 * Generate the comping track for a form. `chords` must be in playback-event
 * order (indices are how the player finds the fingering to strike).
 */
export function generateComping(chords: CompChordSpan[], opts: CompOptions): CompHit[] {
  const style = opts.style;
  if (style === 'held' || chords.length === 0) return [];
  const beatsPerBar = opts.beatsPerBar ?? 4;
  const amount = opts.amount ?? 0.5;
  const barOffset = opts.barOffset ?? 0;
  if (style === 'beat-two') {
    const hits = generateComping(chords, { ...opts, style: 'interactive' });
    // Keep Interactive's waltz vocabulary and chord-following meter fallback.
    return applyBeatTwoAnswers(chords, hits, opts);
  }
  const totalBeats = Math.max(...chords.map((c) => c.startBeat + c.durationBeats));

  // The bar figures are 4/4 vocabulary on a uniform grid; other meters and
  // mid-form meter changes fall back to pushes. The interactive engine also
  // speaks 3/4 (the jazz-waltz vocabulary), and 'sparse' follows chord spans
  // like push does, so any meter is fine there.
  const meterOk =
    beatsPerBar === 4 ||
    ((style === 'interactive' || style === 'funk') && beatsPerBar === 3) ||
    style === 'sparse';
  const effective =
    (!meterOk || (opts.mixedMeter && style !== 'sparse')) && style !== 'push' ? 'push' : style;

  if (effective === 'sparse') {
    // Mostly held: the Held-chords texture — one strike per chord, ringing to
    // the change — with the OCCASIONAL embellishment so a whole chorus of
    // pads still breathes. Two devices, both amount-gated and deterministic:
    // a change sometimes ANTICIPATED by an eighth (Crook III.4 — the tied
    // upbeat is the idiom's one ornament), and a long chord softly RE-STATED
    // on the "and" before its midpoint bar, so a two-bar pad is not two bars
    // of decay. At amount 0 this is exactly Held chords.
    const hits: CompHit[] = [];
    chords.forEach((c, i) => {
      // Chords inside the anacrusis get no comp at all.
      if (c.startBeat < barOffset - 1e-6) return;
      const first = c.startBeat <= barOffset + 1e-6;
      // The form's first chord lands on its beat; a later change is
      // occasionally pushed — never back across the pickup boundary.
      const pushed = !first && spread(i, amount * 0.5, 0.37);
      hits.push({
        chordIndex: i,
        beat: pushed ? Math.max(barOffset, c.startBeat - 0.5) : c.startBeat,
        durationBeats: Infinity,
        velocity: first || !pushed ? 1 : 0.95,
      });
      // A chord holding two bars or more earns one gentle mid-flight
      // re-statement — chord-relative, so mixed meters need no special case.
      const midBars = Math.floor(c.durationBeats / beatsPerBar / 2);
      if (
        midBars >= 1 &&
        c.durationBeats >= 2 * beatsPerBar - 1e-6 &&
        spread(i, amount * 0.7, 0.81)
      ) {
        const at = c.startBeat + midBars * beatsPerBar - 0.5;
        hits.push({ chordIndex: i, beat: at, durationBeats: Infinity, velocity: 0.8 });
      }
    });
    return hits.sort((a, b) => a.beat - b.beat);
  }

  if (effective === 'interactive' || effective === 'funk') {
    const funk = beatsPerBar === 4 && (effective === 'funk' || opts.feel === 'funk');
    // The rhythm articulates the selected voicing. Automatically lifting
    // inversions or dropping the left hand on repeated attacks changed the
    // voice-led line and could leave RH fragments over an already damped LH.
    // Explicit hand/lift events remain available to authored arrangements.
    return planCompRhythm(chords, {
      barOffset,
      amount,
      bpm: opts.bpm,
      melody: opts.melody,
      passStarts: opts.passStarts,
      passKinds: opts.passKinds,
      feel: funk ? 'funk' : opts.feel,
      beatsPerBar,
    }).flatMap((attack) => {
      const chordIndex = chordIndexAt(chords, attack.beat, funk);
      if (chordIndex < 0) return [];
      // An attack never rings past the chord it is bound to: the planner
      // rings each attack to the next one, and across an unsounded change
      // that held the OLD harmony under the new chord (measured at ~47 beats
      // of smear over a 3-pass standard before this). An anticipating attack
      // is bound to the NEXT chord, so it rings across the barline as meant.
      const bound = chords[chordIndex];
      const boundEnd = bound.startBeat + bound.durationBeats;
      const durationBeats = Math.min(attack.durationBeats, Math.max(0, boundEnd - attack.beat));
      return [
        {
          chordIndex,
          beat: attack.beat,
          durationBeats,
          velocity: attack.velocity,
          hand: 'both' as const,
          ...(funk ? { swing: false as const } : {}),
          ...(attack.rollSec !== undefined ? { rollSec: attack.rollSec } : {}),
        },
      ];
    });
  }

  if (effective === 'push') {
    return chords.flatMap((c, i) => {
      // Chords inside the anacrusis get no comp at all.
      if (c.startBeat < barOffset - 1e-6) return [];
      // The form's first chord lands on its beat; every later change is
      // pushed — but never back across the pickup boundary.
      const first = c.startBeat <= barOffset + 1e-6;
      return [
        {
          chordIndex: i,
          beat: first ? c.startBeat : Math.max(barOffset, c.startBeat - 0.5),
          durationBeats: Infinity,
          velocity: first ? 1 : 0.95,
        },
      ];
    });
  }

  const hits: CompHit[] = [];
  for (let barStart = barOffset; barStart < totalBeats; barStart += beatsPerBar) {
    const barIndex = (barStart - barOffset) / beatsPerBar;
    let figure: number[];
    if (effective === 'charleston') figure = [0, 1.5];
    else if (effective === 'reverse') figure = barIndex === 0 ? [0, 1.5, 3] : [1.5, 3];
    else figure = FIGURES[pickFor(barIndex, FIGURES.length, 0.47)];
    hits.push(...hitsFromFigure(chords, barStart, beatsPerBar, figure));
  }

  if (effective === 'freestyle') {
    // Approach stabs: some chord changes get the same shape a fret above or
    // below on the eighth before, resolving onto the real chord at the change.
    chords.forEach((c, i) => {
      // No stab may fire into the anacrusis, nor before the form's first chord.
      if (i === 0 || c.startBeat <= barOffset + 1e-6) return;
      if (!spread(i, amount, 0.29)) return;
      const at = c.startBeat - 0.5;
      // Replace any figure hit on that eighth, and guarantee the resolution.
      const clashing = hits.findIndex((h) => Math.abs(h.beat - at) < 0.26);
      if (clashing >= 0) hits.splice(clashing, 1);
      hits.push({
        chordIndex: i,
        beat: at,
        durationBeats: 0.42,
        velocity: 0.8,
        fretShift: spread(i, 0.5, 0.77) ? 1 : -1,
      });
      if (!hits.some((h) => h.beat >= c.startBeat && h.beat < c.startBeat + 0.26)) {
        hits.push({ chordIndex: i, beat: c.startBeat, durationBeats: 1.4, velocity: 1 });
      }
    });
  }

  return hits.sort((a, b) => a.beat - b.beat);
}

// --- segmented comping (#174) ------------------------------------------------

/** One stretch of the form comped in its own style. */
export interface CompSegment {
  /** Absolute in-form beat this style takes effect. */
  fromBeat: number;
  style: CompStyle;
  /** Freestyle density for this segment; falls back to the global amount. */
  amount?: number;
  /** This segment's time-feel (interactive style): a Latin feel-cue span
   * quotes repetitive figures while the swing spans converse. Falls back to
   * the global feel. */
  feel?: CompFeel;
}

/**
 * Comp a form whose pattern changes mid-tune.
 *
 * Unlike the bass — where one walk resolves its style per chord so the line
 * stays continuous — comp figures are laid on a bar grid by whole-form
 * generators, so each segment genuinely is generated separately. Three things
 * make that safe:
 *
 *   1. GLOBAL CHORD INDICES. `CompHit.chordIndex` addresses the player's
 *      events array. A segment is a slice, so its local indices are shifted
 *      back to the originals — otherwise every hit after the first boundary
 *      strikes the wrong chord.
 *   2. THE GRID STAYS GLOBAL. `barOffset` and the absolute chord beats are
 *      passed through unchanged, so a segment's figures sit on the tune's
 *      barlines rather than restarting a bar grid at the cue.
 *   3. BOUNDARY DAMPING. A held hit (`Infinity`, or one that would ring past
 *      the cue) is truncated AT the boundary, so the outgoing pattern is not
 *      still ringing underneath the incoming one. Deterministic, not a fade.
 *
 * A chord is comped by the segment its ONSET falls in: a chord does not change
 * feel halfway through sounding.
 *
 * A Held segment STRIKES its chords. Bare `generateComping` answers Held with
 * no hits, which is right for a whole form (the player's `undefined` fallback
 * strums each chord), but the player reads a DEFINED hit list as every attack
 * there is, so a Held span beside a figured one would otherwise fall silent.
 */
export function generateSegmentedComping(
  chords: CompChordSpan[],
  opts: CompOptions,
  segments: readonly CompSegment[],
): CompHit[] {
  const ordered = [...segments].sort((a, b) => a.fromBeat - b.fromBeat);
  if (ordered.length <= 1) {
    return generateComping(chords, ordered.length ? { ...opts, ...styleOf(ordered[0]) } : opts);
  }

  const out: CompHit[] = [];
  ordered.forEach((segment, si) => {
    const end = ordered[si + 1]?.fromBeat ?? Infinity;
    // Chords whose ONSET is inside this segment, with their global positions.
    const slice: CompChordSpan[] = [];
    let firstIndex = -1;
    chords.forEach((chord, index) => {
      if (chord.startBeat < segment.fromBeat - 1e-6 || chord.startBeat >= end - 1e-6) return;
      if (firstIndex < 0) firstIndex = index;
      slice.push(chord);
    });
    if (!slice.length) return;

    const hits =
      segment.style === 'held'
        ? heldStrikes(slice, opts.barOffset ?? 0)
        : generateComping(slice, { ...opts, ...styleOf(segment) });
    for (const hit of hits) {
      const beat = hit.beat;
      // A long chord can outlast the feel which owned its onset. Its
      // outgoing figure must not schedule new attacks after that cue.
      if (beat >= end) continue;
      // A hit pushed back across the boundary belongs to the previous
      // segment's territory; clamp rather than let it sound early.
      const clamped = Math.max(segment.fromBeat, beat);
      const ringsPast = clamped + hit.durationBeats > end;
      out.push({
        ...hit,
        chordIndex: hit.chordIndex + firstIndex,
        beat: clamped,
        durationBeats: ringsPast ? end - clamped : hit.durationBeats,
      });
    }
  });
  return out.sort((a, b) => a.beat - b.beat);
}

/** Held chords as hits: one strike per chord on its onset, ringing to the change. */
function heldStrikes(chords: CompChordSpan[], barOffset: number): CompHit[] {
  return chords.flatMap((chord, chordIndex) =>
    // Chords inside the anacrusis get no comp at all.
    chord.startBeat < barOffset - 1e-6
      ? []
      : [{ chordIndex, beat: chord.startBeat, durationBeats: Infinity, velocity: 1 }],
  );
}

function styleOf(segment: CompSegment): Pick<CompOptions, 'style' | 'amount' | 'feel'> {
  return {
    style: segment.style,
    ...(segment.amount !== undefined ? { amount: segment.amount } : {}),
    ...(segment.feel !== undefined ? { feel: segment.feel } : {}),
  };
}
